/**
 * Theme Registry
 *
 * Const object registry pattern for theme management.
 * Uses compile-time type generation for maximum type safety.
 *
 * CRITICAL: This uses a const object (NOT Map) to:
 * - Avoid side effects during HMR
 * - Enable compile-time type checking
 * - Support tree-shaking
 * - Maintain zero runtime overhead
 *
 * SYNC: Keep in sync with ThemeId enum in identifiers.ts
 */

import { ThemeDefinition } from './types';
import { ThemeId } from './identifiers';
import { defaultTheme, defaultMetadata } from './default.theme';
import { rubyTheme, rubyMetadata } from './ruby.theme';
import { oceanTheme, oceanMetadata } from './ocean.theme';
import { forestTheme, forestMetadata } from './forest.theme';
import { sunshineTheme, sunshineMetadata } from './sunshine.theme';
import { roseTheme, roseMetadata } from './rose.theme';
import { aquaTheme, aquaMetadata } from './aqua.theme';
import { twilightTheme, twilightMetadata } from './twilight.theme';
import { amberTheme, amberMetadata } from './amber.theme';
import { skyTheme, skyMetadata } from './sky.theme';
import { limeTheme, limeMetadata } from './lime.theme';
import { lavenderTheme, lavenderMetadata } from './lavender.theme';
import { jadeTheme, jadeMetadata } from './jade.theme';

/**
 * Registered themes const object
 * Add new themes here to make them available throughout the app.
 *
 * NOTE: The standalone `dark` theme has been retired. Each theme now gets
 * light/medium/dark variants via applyVariant() in variant-transform.ts.
 */
export const REGISTERED_THEMES = {
  [ThemeId.DEFAULT]: { palette: defaultTheme, metadata: defaultMetadata },
  [ThemeId.RUBY]: { palette: rubyTheme, metadata: rubyMetadata },
  [ThemeId.OCEAN]: { palette: oceanTheme, metadata: oceanMetadata },
  [ThemeId.FOREST]: { palette: forestTheme, metadata: forestMetadata },
  [ThemeId.SUNSHINE]: { palette: sunshineTheme, metadata: sunshineMetadata },
  [ThemeId.ROSE]: { palette: roseTheme, metadata: roseMetadata },
  [ThemeId.AQUA]: { palette: aquaTheme, metadata: aquaMetadata },
  [ThemeId.TWILIGHT]: { palette: twilightTheme, metadata: twilightMetadata },
  [ThemeId.AMBER]: { palette: amberTheme, metadata: amberMetadata },
  [ThemeId.SKY]: { palette: skyTheme, metadata: skyMetadata },
  [ThemeId.LIME]: { palette: limeTheme, metadata: limeMetadata },
  [ThemeId.LAVENDER]: { palette: lavenderTheme, metadata: lavenderMetadata },
  [ThemeId.JADE]: { palette: jadeTheme, metadata: jadeMetadata },
} as const satisfies Record<string, ThemeDefinition>;

/**
 * Type-safe theme name (compile-time!)
 * Automatically derived from REGISTERED_THEMES keys
 */
export type ThemeName = keyof typeof REGISTERED_THEMES;

/**
 * Get a specific theme by name
 *
 * @param name - Theme name from ThemeId enum
 * @returns Complete theme definition with palette and metadata
 *
 * @example
 * ```typescript
 * const theme = getTheme(ThemeId.DEFAULT);
 * console.log(theme.palette.primary); // '#B85C00'
 * ```
 */
export function getTheme(name: ThemeName): ThemeDefinition {
  return REGISTERED_THEMES[name];
}

/**
 * Get all registered themes with names
 * Useful for auto-generating theme selectors in UI
 *
 * @returns Array of themes with their names
 *
 * @example
 * ```typescript
 * const themes = getAllThemes();
 * themes.forEach(t => {
 *   console.log(t.name, t.metadata.label);
 * });
 * ```
 */
export function getAllThemes(): Array<ThemeDefinition & { name: ThemeName }> {
  return Object.entries(REGISTERED_THEMES).map(([name, def]) => ({
    ...def,
    name: name as ThemeName,
  }));
}

/**
 * Type guard to check if a string is a valid theme name
 *
 * @param name - String to check
 * @returns True if name is a registered theme
 *
 * @example
 * ```typescript
 * const userTheme: string = 'dark';
 * if (hasTheme(userTheme)) {
 *   // TypeScript knows userTheme is ThemeName here
 *   const theme = getTheme(userTheme);
 * }
 * ```
 */
export function hasTheme(name: string): name is ThemeName {
  return name in REGISTERED_THEMES;
}
